/**
 * Mutex produit : cagnotte FCFA (config.loyalty) ⊕ module carte Pass (feature loyaltyPass).
 * Les deux ne doivent pas être actifs ensemble.
 *
 * « Module prêt » métier = feature contrat + ≥ 1 règle active (pas seulement la feature).
 */

import type { Business } from '../../types/business';
import { isFeatureEnabled, isLoyaltyEnabled } from '../../types/business';
import { normalizeLoyaltyPassRules } from './rulesConfig';

/** True si la cagnotte est on et que la feature carte Pass est aussi on. */
export function isLoyaltyPassConflictingWithCagnotte(business: Business | null): boolean {
  if (!business) return false;
  return isFeatureEnabled(business, 'loyaltyPass') && isLoyaltyEnabled(business);
}

/** True si on peut utiliser le module carte sans conflit cagnotte. */
export function canUseLoyaltyPassWithoutCagnotteConflict(business: Business | null): boolean {
  if (!business) return false;
  if (!isFeatureEnabled(business, 'loyaltyPass')) return false;
  return !isLoyaltyEnabled(business);
}

/** Mode d’indice fidélité dans le sélecteur client (caisse). */
export type ClientPosLoyaltyHintMode = 'wallet' | 'pass' | 'none';

/**
 * Affichage sélecteur client : carte BonSpot si le module est **opérationnel**
 * (feature + ≥1 règle), sinon cagnotte.
 */
export function resolveClientPosLoyaltyHintMode(
  business: Business | null
): ClientPosLoyaltyHintMode {
  if (!business) return 'none';
  if (isLoyaltyPassOperational(business)) return 'pass';
  if (isLoyaltyEnabled(business)) return 'wallet';
  return 'none';
}

/** Empêche d’activer la carte si la cagnotte est déjà on. */
export function canActivateLoyaltyPassFeature(business: Business | null): boolean {
  if (!business) return false;
  return !isLoyaltyEnabled(business);
}

/** Empêche d’activer la cagnotte si la carte Pass est déjà on. */
export function canActivateCagnotteLoyalty(business: Business | null): boolean {
  if (!business) return false;
  return !isFeatureEnabled(business, 'loyaltyPass');
}

/** Feature contrat seule (Paramètres / Super Admin / page config règles). */
export function isLoyaltyPassFeatureOn(business: Business | null): boolean {
  return Boolean(business && isFeatureEnabled(business, 'loyaltyPass'));
}

/** Au moins une règle `enabled: true` dans la config programme. */
export function hasAtLeastOneLoyaltyPassActiveRule(
  business: Business | null
): boolean {
  if (!business?.loyaltyPassProgram) return false;
  const rules = normalizeLoyaltyPassRules(business.loyaltyPassProgram.rules);
  return rules.some((r) => r.enabled);
}

/**
 * Module prêt côté métier : feature **et** ≥ 1 règle active.
 * À utiliser partout (caisse, création carte, hints) sauf l’écran de config des règles.
 */
export function isLoyaltyPassOperational(business: Business | null): boolean {
  return (
    isLoyaltyPassFeatureOn(business) && hasAtLeastOneLoyaltyPassActiveRule(business)
  );
}

/** Création / offre carte possible = opérationnel + merchant lié. */
export function isLoyaltyPassCardOfferAvailable(business: Business | null): boolean {
  return getLoyaltyPassCardOfferBlockReason(business) === null;
}

/**
 * Caisse : tenter visite / statut / bridge après vente.
 * Feature + ≥1 règle + merchant — sinon **aucun** appel BonSpot.
 */
export function shouldAttemptLoyaltyPassVisitOnSale(
  business: Business | null
): boolean {
  if (!isLoyaltyPassOperational(business)) return false;
  const merchantId =
    typeof business?.bonspotMerchantId === 'string'
      ? business.bonspotMerchantId.trim()
      : '';
  return Boolean(merchantId);
}

/**
 * Raison i18n si la case/CTA doit rester visible mais désactivée.
 * `null` = création possible.
 */
export function getLoyaltyPassCardOfferBlockReason(
  business: Business | null
):
  | 'loyaltyPass.clientDetail.createCardBlockedFeature'
  | 'loyaltyPass.clientDetail.createCardBlockedNoRules'
  | 'loyaltyPass.clientDetail.createCardBlockedMerchant'
  | null {
  if (!business || !isFeatureEnabled(business, 'loyaltyPass')) {
    return 'loyaltyPass.clientDetail.createCardBlockedFeature';
  }
  if (!hasAtLeastOneLoyaltyPassActiveRule(business)) {
    return 'loyaltyPass.clientDetail.createCardBlockedNoRules';
  }
  const merchantId =
    typeof business.bonspotMerchantId === 'string'
      ? business.bonspotMerchantId.trim()
      : '';
  if (!merchantId) {
    return 'loyaltyPass.clientDetail.createCardBlockedMerchant';
  }
  return null;
}
